import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { AppConfig } from '../../../core';

export default function Privacy() {
  const { palette, typography } = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: '64px',
      }}
    >
      <Helmet>
        <title>Privacy | {AppConfig.appName}</title>
        <meta name="description" content="Privacy policy for LegacyTies" />
      </Helmet>
      <Box
        sx={{
          width: '100%',
          height: 300,
          backgroundColor: palette.primary.main,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{
              color: palette.primary.contrastText,
              lineHeight: 1.25,
              fontSize: {
                xs: typography.body1.fontSize,
                md: typography.h2.fontSize,
              },
              fontWeight: { xs: 'none', md: typography.h1.fontWeight },
            }}
          >
            Privacy Policy
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: palette.primary.contrastText }}
          >
            Last updated: 11 March 2026
          </Typography>
        </Container>
      </Box>

      <Box sx={{ width: '100%', py: 4, bgcolor: palette.background.paper }}>
        <Container maxWidth="lg">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            <strong>1. Introduction</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Welcome to {AppConfig.appName}. {AppConfig.appName} is a platform
            designed to help African diaspora families build family trees,
            preserve cultural traditions, and document migration history. This
            Privacy Policy explains how we collect, use, and protect your
            personal data when you use our platform.
          </Typography>
          <Typography variant="subtitle1">
            This policy complies with the UK General Data Protection Regulation
            and the Data Protection Act 2018.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>2. Data Controller</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            The data controller responsible for your personal information is:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              <strong>{AppConfig.appName}</strong>
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              <strong>Email:</strong> {AppConfig.appContactEmail}
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>3. Information We Collect</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            We collect the following categories of information:
          </Typography>

          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 700 }}>
            <strong>3.1 Personal Information</strong>
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Name and email address when creating an account.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Profile details provided by the user.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Family member information entered into family trees.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Migration history information voluntarily shared.
            </ListItem>
          </List>

          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            <strong>3.2 User-Generated Content</strong>
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Family stories and oral histories.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Recipes, traditions, and cultural knowledge uploaded to the
              Cultural Hub.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Photos, videos, and documents uploaded by users.
            </ListItem>
          </List>

          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            <strong>3.3 Special Category Data</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ my: 1 }}>
            Some data you upload may constitute special category data under UK
            GDPR, including:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Photographs of living individuals.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Health or genetic information that may appear in family records.
            </ListItem>
          </List>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            We only process special category data with your explicit consent.
            You will be asked to provide explicit consent where required.
          </Typography>

          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 700 }}>
            <strong>3.4 Technical Data</strong>
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              IP address and device identifiers.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Browser type and device information.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Usage analytics and cookies.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>4. Legal Basis for Processing</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            We process personal data under the following legal bases:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              <strong>Contract:</strong> to provide your {AppConfig.appName}{' '}
              account and platform services.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              <strong>Legitimate interests:</strong> to improve the platform and
              ensure security.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              <strong>Consent:</strong> for analytics cookies and optional
              features.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              <strong>Legal obligation:</strong> where required by law.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>5. How We Use Your Information</strong>
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Provide family tree creation and visualisation tools, cultural
              content sharing, and migration tracking.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Enable collaboration between family members.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Preserve cultural traditions and heritage content.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Improve platform performance and functionality.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Maintain security and prevent unauthorised access.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Comply with our legal obligations.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>6. Data Sharing</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            We do not sell personal data. We may share data with:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Family members you choose to collaborate with.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Trusted service providers, such as hosting, analytics, and
              infrastructure providers.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Legal authorities if required by law.
            </ListItem>
          </List>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            Your family tree data is never publicly visible unless you choose to
            share it.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>7. International Data Transfers</strong>
          </Typography>
          <Typography variant="subtitle1">
            Some services used to operate {AppConfig.appName} may process data
            outside the United Kingdom. Where this occurs, appropriate
            safeguards such as Standard Contractual Clauses are used to protect
            personal data.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>8. Data Retention</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            We retain personal data only for as long as necessary to provide the
            service.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Account data is retained until the account is deleted.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Family tree data remains until it is removed by users.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Analytics data is retained for a limited period.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>9. Data Security</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            We implement technical and organisational measures to protect
            personal data, including:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Encrypted data transmission in transit (TLS/HTTPS) and at rest.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Access controls to ensure only authorised personnel can access
              personal data.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Secure password hashing; we do not store plain-text passwords.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Security monitoring and audits.
            </ListItem>
          </List>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            While we take all reasonable precautions, no internet transmission
            is completely secure. You are responsible for keeping your account
            credentials confidential.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>10. Your Rights</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Under the UK GDPR, you have the following rights:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Access your personal data.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Correct inaccurate information.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Request deletion of your data.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Restrict or object to processing.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Request data portability.
            </ListItem>
          </List>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            You also have the right to lodge a complaint with the Information
            Commissioner’s Office.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>11. Children’s Data</strong>
          </Typography>
          <Typography variant="subtitle1">
            {AppConfig.appName} may include information about family members who
            are minors as part of family trees. Users should ensure they have
            appropriate permission before adding information about living
            individuals. If we become aware that we have collected data about a
            child without appropriate consent, we will delete it promptly.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>12. Cookies</strong>
          </Typography>
          <Typography variant="subtitle1">
            We use cookies and similar technologies on our platform. For full
            details of the cookies we use and how to manage your preferences,
            please see our Cookie Policy at
            https://legacy-ties.com/cookie-policy.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>13. Data Export and Portability</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {AppConfig.appName} supports data portability. Users may request a
            copy of their personal data or export their family tree information
            in commonly used genealogy formats where available.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Users can export their family tree records.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Export formats may include widely used genealogy standards such as
              GEDCOM.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Exported data can be used for personal archives or transferred to
              other genealogy services.
            </ListItem>
          </List>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            This feature supports your right to data portability under the UK
            General Data Protection Regulation.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>14. Cultural Heritage Respect</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {AppConfig.appName} is built to help families preserve and celebrate
            their heritage. We recognise that cultural traditions, family
            histories, and ancestral knowledge hold deep personal and historical
            significance.
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            We commit to the following principles:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Cultural stories, traditions, and family histories shared on{' '}
              {AppConfig.appName} remain the property of the users who upload
              them.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              {AppConfig.appName} does not sell, commercially exploit, or
              transfer cultural heritage content to third parties.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Cultural Hub section of {AppConfig.appName} content is intended
              for educational and heritage preservation purposes, and we
              encourage respectful sharing and collaboration.
            </ListItem>
          </List>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            Our mission is to provide a respectful space where African diaspora
            families can document and protect their heritage for future
            generations.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>15. Changes to This Policy</strong>
          </Typography>
          <Typography variant="subtitle1">
            We may update this Privacy Policy to reflect platform improvements
            or legal changes. Significant updates will be communicated via the
            platform.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, fontWeight: 700 }}>
            <strong>16. Contact</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            For questions about this Privacy Policy or your data rights, please
            contact:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              <strong>Email:</strong> {AppConfig.appContactEmail}
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Website: https://legacy-ties.com
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Response time: we aim to respond within 5 business days.
            </ListItem>
          </List>
        </Container>
      </Box>
    </Box>
  );
}
